import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import store from "../../../Store/store";
import { checkUserDetailsFromCookie, fetchStoreLevelCompanies } from "../../../api_calls/userManagement";
import { fetchCompanyHierarchy } from "../../../api_calls/companyRequests";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import StoreOwnerHierarchySelect, { buildStoreOwnerGroups } from "./StoreOwnerHierarchySelect";

// Branch keeps the flat share-list. Cyreen and tenant get retailer groups from
// the company hierarchy. Agency, retailer, and brand only see their assigned company.
export const HIERARCHY_OWNER_USER_TYPES = ["cyreen", "tenant"];
export const FLAT_OWNER_USER_TYPES = ["branch"];
const EMPTY_RETAILERS = [];

const loggedInStoreOwnerUserType = () => {
  const loggedUserDetails = checkUserDetailsFromCookie(false);
  const rawType = loggedUserDetails?.user_detail?.user_type;
  return typeof rawType === "string" ? rawType.toLowerCase() : "";
};

// One owner request per open. The other list is unused for this user, so it is not loaded.
export const fetchStoreOwnerOptions = () => {
  const userType = loggedInStoreOwnerUserType();
  if (FLAT_OWNER_USER_TYPES.includes(userType)) {
    return fetchStoreLevelCompanies("stores");
  }
  if (HIERARCHY_OWNER_USER_TYPES.includes(userType)) {
    return fetchCompanyHierarchy();
  }
  return Promise.resolve({ success: true });
};

const StoreOwnerField = ({ fieldError, elemId = "ownerSelectionForStore" }) => {
  const userStateDetails = useSelector((state) => state.userSelections);
  const loggedUserDetails = checkUserDetailsFromCookie();
  const dispatchAction = useDispatch();

  const userType = loggedInStoreOwnerUserType();
  const usesHierarchy = HIERARCHY_OWNER_USER_TYPES.includes(userType);
  const usesFlatList = FLAT_OWNER_USER_TYPES.includes(userType);

  const hierarchyLoaded = typeof userStateDetails.company_hierarchy !== "undefined";
  const hierarchyRetailers =
    hierarchyLoaded === true && Array.isArray(userStateDetails.company_hierarchy.retailers)
      ? userStateDetails.company_hierarchy.retailers
      : EMPTY_RETAILERS;
  // Retailer roots are parents. Only the companies under them can be saved.
  const ownerGroups = React.useMemo(() => buildStoreOwnerGroups(hierarchyRetailers), [hierarchyRetailers]);
  const ownerCompanies = React.useMemo(
    () => ownerGroups.flatMap((group) => group.companies),
    [ownerGroups]
  );
  const showOwnerGroups = usesHierarchy === true && ownerCompanies.length > 1;

  const assignedCompany =
    loggedUserDetails.success === true ? loggedUserDetails.user_detail.assigned_company : undefined;
  const selection = userStateDetails?.new_store_loc_data?.share_list_selection;
  const hierarchyValue =
    selection !== null &&
    typeof selection === "object" &&
    typeof selection.id !== "undefined" &&
    selection.id !== ""
      ? { id: selection.id, label: selection.label }
      : null;

  const writeOwner = (storeLocData) => {
    const existing =
      typeof userStateDetails.new_store_loc_data !== "undefined" ? userStateDetails.new_store_loc_data : {};
    dispatchAction(updateStateData({ new_store_loc_data: { ...existing, ...storeLocData } }));
  };

  // Add starts empty when more than one company can be chosen. One company is
  // hidden, so that id is written here or save would ask for a selection. An id
  // already set (edit prefill, or a previous choice) is left alone.
  React.useEffect(() => {
    if (usesHierarchy !== true || ownerCompanies.length !== 1) {
      return;
    }
    const onlyCompany = ownerCompanies[0];
    const current = store.getState().userSelections;
    const currentId = current?.new_store_loc_data?.share_list;
    if (currentId !== "" && currentId !== null && typeof currentId !== "undefined") {
      return;
    }
    const existing = typeof current.new_store_loc_data !== "undefined" ? current.new_store_loc_data : {};
    dispatchAction(
      updateStateData({
        new_store_loc_data: {
          ...existing,
          share_list: onlyCompany.id,
          share_list_selection: { id: onlyCompany.id, label: onlyCompany.label },
        },
      })
    );
  }, [usesHierarchy, ownerCompanies, dispatchAction]);

  if (usesHierarchy === true && hierarchyLoaded === true && ownerCompanies.length <= 1) {
    return null;
  }

  if (showOwnerGroups === true || usesHierarchy === true) {
    return (
      <StoreOwnerHierarchySelect
        label="Owner*"
        elemId={elemId}
        groups={ownerGroups}
        value={hierarchyValue}
        fieldError={fieldError}
        onChange={(selectedOption) => {
          if (selectedOption === null) {
            writeOwner({
              share_list: "",
              share_list_selection: null,
            });
            return;
          }
          writeOwner({
            share_list: selectedOption.id,
            share_list_selection: { id: selectedOption.id, label: selectedOption.label },
          });
        }}
      />
    );
  }

  if (usesFlatList === true) {
    return (
      <SingleSelect
        label="Owner*"
        elemId={elemId}
        enableFullWidth={true}
        readOnlyFlag={false}
        options={
          typeof userStateDetails.store_level_retailers_list !== "undefined"
            ? userStateDetails.store_level_retailers_list
            : []
        }
        defaultVal={
          selection !== null && typeof selection === "object" && typeof selection.id !== "undefined"
            ? selection
            : {}
        }
        selectionCheck={(option, value) => option?.id === value?.id}
        onChangeCall={(selectedOption) => {
          writeOwner({
            share_list: selectedOption.id,
            share_list_selection: selectedOption,
          });
        }}
        fieldError={fieldError}
      />
    );
  }

  // Read-only. Save uses assigned_company.id and ignores this display value.
  return (
    <SingleSelect
      label="Owner*"
      elemId={elemId}
      enableFullWidth={true}
      readOnlyFlag={true}
      options={
        typeof assignedCompany !== "undefined"
          ? [{ label: assignedCompany.name, id: assignedCompany.id }]
          : []
      }
      defaultVal={
        typeof assignedCompany !== "undefined"
          ? { label: assignedCompany.name, id: assignedCompany.id }
          : {}
      }
      selectionCheck={(option, value) => option?.id === value?.id}
      onChangeCall={() => {}}
      fieldError={fieldError}
    />
  );
};

export default StoreOwnerField;
